import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./AddProduct.css";
import { useTitle } from "../../hooks/useTitle";
import {
  tablesDB,
  storage,
  DB_ID,
  PRODUCTS_TABLE,
  USERS_TABLE,
  BUCKET_ID,
  account,
} from "../../appwrite";
import { Permission, Role, ID, Query } from "appwrite";
import { ImagePlus, X, Star } from "lucide-react";

const CATEGORY_CODES = { "reup-vault": "RUV" };

const generateSlug = (name) =>
  name
    .toString()
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+$/g, "");

const keywordMap = {
  new: ["new", "brand new", "sealed"],
  used: ["used", "fairly used", "second hand"],
  "like-new": ["like new", "barely used"],
  "open-box": ["open box", "no box"],
  "for-parts": ["for parts", "faulty", "not working"],
  authentic: ["original", "authentic", "genuine"],
  fake: ["fake", "replica", "copy", "knockoff"],
  refurbished: ["refurbished", "renewed"],
  "uk-used": ["uk used", "london used"],
  jordans: ["jordans", "yeezy", "air max", "dunk"],
  phone: ["phone", "iphone", "samsung"],
  gaming: ["ps5", "playstation", "xbox"],
  laptop: ["laptop", "macbook", "dell", "hp"],
  luxury: ["designer", "gucci", "lv", "prada"],
  perfume: ["perfume", "fragrance"],
};

const extractTags = (text) => {
  const lower = text.toLowerCase();
  const tags = new Set();
  Object.entries(keywordMap).forEach(([tag, keywords]) => {
    keywords.forEach((kw) => {
      if (lower.includes(kw)) tags.add(tag);
    });
  });
  return Array.from(tags);
};

export default function AddProduct() {
  useTitle("Add Product - TrendBay Seller");
  const navigate = useNavigate();

  const [product, setProduct] = useState({
    productName: "",
    productDescription: "",
    productCondition: "",
    price: "",
    quantity: "",
    sku: "",
    category: "",
    available: true,
    featured: true,
  });
  const [images, setImages] = useState([]); // { file, preview }[]
  const [trustTags, setTrustTags] = useState({
    authentic: true,
    withBox: false,
    withReceipt: false,
    sealed: false,
  });
  const [saving, setSaving] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const imageInputRef = useRef();

  useEffect(() => {
    const meta = document.querySelector("meta[name='theme-color']");
    const orig = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", "#080F0D");
    return () => {
      if (meta) meta.setAttribute("content", orig);
    };
  }, []);

  const formatPrice = (val) => {
    const n = parseFloat((val || "").replace(/,/g, ""));
    return isNaN(n)
      ? ""
      : n.toLocaleString("en-NG", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
  };

  const generateSKU = async (category) => {
    const code = CATEGORY_CODES[category];
    if (!code) return "";
    let sku, exists;
    do {
      sku = `${code}${Math.floor(100 + Math.random() * 900)}`;
      const res = await tablesDB.listRows({
        databaseId: DB_ID,
        tableId: PRODUCTS_TABLE,
        queries: [Query.equal("sku", sku)],
      });
      exists = (res.rows || []).length > 0;
    } while (exists);
    return sku;
  };

  const handleField = async (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "category") {
      const sku = value ? await generateSKU(value) : "";
      setProduct((p) => ({ ...p, category: value, sku }));
    } else if (name === "price") {
      setProduct((p) => ({ ...p, price: formatPrice(value) }));
    } else if (type === "checkbox") {
      setProduct((p) => ({ ...p, [name]: checked }));
    } else {
      setProduct((p) => ({ ...p, [name]: value }));
    }
  };

  const handleTrustTag = (e) => {
    const { name, checked } = e.target;
    if (name === "authentic" && checked) {
      toast.warn(
        "You confirm this item is original and as described. Misrepresentation may lead to a ban and legal action.",
      );
    }
    setTrustTags((t) => ({ ...t, [name]: checked }));
  };

  const readFile = (file) =>
    new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(file);
    });

  const addImages = async (files) => {
    const entries = await Promise.all(
      Array.from(files).map(async (file) => ({
        file,
        preview: await readFile(file),
      })),
    );
    setImages((prev) => [...prev, ...entries]);
  };

  const removeImage = (idx) =>
    setImages((prev) => prev.filter((_, i) => i !== idx));

  const makeCover = (idx) => {
    setImages((prev) => {
      const next = [...prev];
      const [picked] = next.splice(idx, 1);
      return [picked, ...next];
    });
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) await addImages(e.dataTransfer.files);
  };

  const handleClear = () => {
    setProduct({
      productName: "",
      productDescription: "",
      productCondition: "",
      price: "",
      quantity: "",
      sku: "",
      category: "",
      available: true,
      featured: true,
    });
    setImages([]);
    setTrustTags({ authentic: true, withBox: false, withReceipt: false, sealed: false });
  };

  const handleSave = () => {
    if (
      !product.productName ||
      !product.productDescription ||
      !product.price ||
      !product.quantity ||
      !product.sku ||
      !product.category ||
      images.length === 0
    ) {
      toast.error("Fill in all required fields and add at least one image.");
      return;
    }
    setShowAuthModal(true);
  };

  const confirmAndList = async () => {
    setShowAuthModal(false);
    setSaving(true);
    try {
      const currentUser = await account.get();
      let sellerName = "Seller";
      try {
        const row = await tablesDB.getRow({
          databaseId: DB_ID,
          tableId: USERS_TABLE,
          rowId: currentUser.$id,
        });
        sellerName = row.fullName || sellerName;
      } catch {}

      const urls = await Promise.all(
        images.map(async ({ file }) => {
          const fileId = ID.unique();
          await storage.createFile(BUCKET_ID, fileId, file, [Permission.read(Role.any())]);
          return `https://storage.trendbayds.com/f/${fileId}`;
        }),
      );

      const [productImage, ...moreImages] = urls;

      const combinedText = `${product.productName} ${product.productDescription}`;
      const tags = extractTags(combinedText);
      if (tags.includes("fake")) {
        toast.error("Listing blocked. Replica items are not allowed.");
        setSaving(false);
        return;
      }

      const selectedTags = Object.keys(trustTags).filter((t) => trustTags[t]);
      let metaNote = "\nNOTE: ";
      if (selectedTags.length > 0) {
        metaNote += `This seller confirms this item is ${selectedTags.join(", ")}. If not as described, contact TB Mart Support.\n`;
      }
      metaNote += `Condition: ${(product.productCondition || "N/A").replace(/-/g, " ")}\nSKU: ${product.sku}\n`;

      await tablesDB.createRow({
        databaseId: DB_ID,
        tableId: PRODUCTS_TABLE,
        rowId: ID.unique(),
        data: {
          productName: product.productName,
          productDescription: `${product.productDescription.trim()}\n\n${metaNote}`,
          price: parseFloat(product.price.replace(/,/g, "")),
          quantity: parseInt(product.quantity || "0", 10),
          sku: product.sku,
          productCondition: product.productCondition || "",
          category: product.category,
          productImage,
          moreImages,
          available: product.available,
          featured: product.featured,
          slug: generateSlug(product.productName),
          sellerId: currentUser.$id,
          sellerName,
          trustTags: Object.keys(trustTags).filter((k) => trustTags[k]),
          sold: false,
        },
        permissions: [
          Permission.read(Role.any()),
          Permission.write(Role.user(currentUser.$id)),
        ],
      });

      toast.success("Product listed!");
      navigate("/s/my-vault");
    } catch (e) {
      console.error(e);
      toast.error("Error listing product. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ap-page">
      {/* Header */}
      <div className="ap-header">
        <div>
          <h2 className="ap-title">Add Product</h2>
          <p className="ap-subtitle">List a new item in your vault</p>
        </div>
        <div className="ap-header-actions">
          <button
            className="ap-btn-ghost"
            onClick={() => navigate("/s/my-vault")}
          >
            Discard
          </button>
          <button
            className="ap-btn-green"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Listing…" : "List Product"}
          </button>
        </div>
      </div>

      <div className="ap-body">
        {/* LEFT */}
        <div className="ap-main">
          {/* Images */}
          <div className="ap-card">
            <h3 className="ap-card-title">Product Images</h3>
            <p className="ap-card-hint">
              First image is the cover. Click ★ on any thumbnail to make it the
              cover.
            </p>
            <div
              className="ap-image-drop"
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => imageInputRef.current.click()}
            >
              <ImagePlus size={28} color="#00A855" />
              <p>
                Drop images here or <span>click to browse</span>
              </p>
              <p className="ap-drop-hint">JPG, PNG, WEBP · Multiple allowed</p>
            </div>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => addImages(e.target.files)}
            />
            {images.length > 0 && (
              <div className="ap-image-grid">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className={`ap-thumb ${idx === 0 ? "ap-thumb--cover" : ""}`}
                  >
                    <img src={img.preview} alt="" />
                    {idx === 0 && <span className="ap-thumb-badge">Cover</span>}
                    <div className="ap-thumb-actions">
                      {idx !== 0 && (
                        <button
                          className="ap-thumb-btn ap-thumb-star"
                          onClick={() => makeCover(idx)}
                          title="Make cover"
                        >
                          <Star size={13} />
                        </button>
                      )}
                      <button
                        className="ap-thumb-btn ap-thumb-remove"
                        onClick={() => removeImage(idx)}
                        title="Remove"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                ))}
                <div
                  className="ap-thumb ap-thumb--add"
                  onClick={() => imageInputRef.current.click()}
                >
                  <ImagePlus size={22} color="#aaa" />
                  <span>Add more</span>
                </div>
              </div>
            )}
          </div>

          {/* Product info */}
          <div className="ap-card">
            <h3 className="ap-card-title">Product Information</h3>
            <div className="ap-field">
              <label className="ap-label">
                Title <span className="ap-req">*</span>
              </label>
              <input
                className="ap-input"
                type="text"
                name="productName"
                value={product.productName}
                onChange={handleField}
                placeholder="e.g. Nike Air Max 90"
              />
            </div>
            <div className="ap-field">
              <label
                className="ap-label"
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <span>
                  Description <span className="ap-req">*</span>
                </span>
                <span
                  style={{
                    fontWeight: 400,
                    color:
                      product.productDescription.length > 950
                        ? "#e74c3c"
                        : "#bbb",
                  }}
                >
                  {product.productDescription.length}/1000
                </span>
              </label>
              <textarea
                className="ap-input ap-textarea"
                name="productDescription"
                value={product.productDescription}
                onChange={handleField}
                rows={6}
                maxLength={1000}
                placeholder="Describe your item - condition, size, age, notes…"
              />
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="ap-side">
          {/* Pricing */}
          <div className="ap-card">
            <h3 className="ap-card-title">Pricing & Details</h3>

            <div className="ap-price-qty-row">
              <div className="ap-field ap-price-col">
                <label className="ap-label">
                  Price (NGN) <span className="ap-req">*</span>
                </label>
                <div className="ap-price-wrap">
                  <span className="ap-currency">₦</span>
                  <input
                    className="ap-input ap-input--price"
                    type="text"
                    name="price"
                    value={product.price}
                    onChange={handleField}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="ap-field ap-qty-col">
                <label className="ap-label">Qty <span className="ap-req">*</span></label>
                <input
                  className="ap-input"
                  type="number"
                  name="quantity"
                  value={product.quantity}
                  onChange={handleField}
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>

            <div className="ap-field">
              <label className="ap-label">
                Category <span className="ap-req">*</span>
              </label>
              <select
                className="ap-input"
                name="category"
                value={product.category}
                onChange={handleField}
              >
                <option value="">Select Category</option>
                <option value="reup-vault">ReUp Vault</option>
              </select>
            </div>

            <div className="ap-field">
              <label className="ap-label">SKU</label>
              <input
                className="ap-input ap-input--muted"
                type="text"
                name="sku"
                value={product.sku}
                readOnly
                placeholder="Auto-generated on category select"
              />
            </div>

            <div className="ap-field">
              <label className="ap-label">Condition</label>
              <select
                className="ap-input"
                name="productCondition"
                value={product.productCondition}
                onChange={handleField}
              >
                <option value="">Select Condition</option>
                <option value="new">New</option>
                <option value="like-new">Like New / No Box</option>
                <option value="used">Used</option>
                <option value="for-parts">For Parts</option>
              </select>
            </div>
          </div>

          {/* Visibility */}
          <div className="ap-card">
            <h3 className="ap-card-title">Visibility</h3>
            <div className="ap-toggle-row">
              <div>
                <p className="ap-toggle-label">Available for sale</p>
                <p className="ap-toggle-hint">Visible to buyers</p>
              </div>
              <label className="ap-switch">
                <input
                  type="checkbox"
                  name="available"
                  checked={product.available}
                  onChange={handleField}
                />
                <span className="ap-switch-track" />
              </label>
            </div>
          </div>

          {/* Trust Tags */}
          <div className="ap-card">
            <h3 className="ap-card-title">Trust Tags</h3>
            <p className="ap-card-hint">
              Confirm details buyers care about. These appear on your listing.
            </p>
            {/* Authentic - always locked on */}
            <div className="ap-toggle-row">
              <div>
                <p className="ap-toggle-label">
                  Authentic / Original{" "}
                  <span
                    style={{ fontSize: 10, color: "#00A855", fontWeight: 700 }}
                  >
                    REQUIRED
                  </span>
                </p>
                <p className="ap-toggle-hint">
                  All items on TrendBay must be genuine. Non-authentic listings
                  will be removed.
                </p>
              </div>
              <label className="ap-switch">
                <input type="checkbox" checked disabled />
                <span className="ap-switch-track" />
              </label>
            </div>

            {[
              ["withBox", "With Box", "Original box included"],
              ["withReceipt", "With Receipt", "Purchase receipt available"],
              ["sealed", "Sealed", "Item is factory sealed"],
            ].map(([k, label, hint]) => (
              <div className="ap-toggle-row" key={k}>
                <div>
                  <p className="ap-toggle-label">{label}</p>
                  <p className="ap-toggle-hint">{hint}</p>
                </div>
                <label className="ap-switch">
                  <input
                    type="checkbox"
                    name={k}
                    checked={trustTags[k]}
                    onChange={handleTrustTag}
                  />
                  <span className="ap-switch-track" />
                </label>
              </div>
            ))}
          </div>

          <div className="ap-side-actions">
            <button className="ap-btn-ghost ap-btn-full" onClick={handleClear}>
              Clear form
            </button>
            <button
              className="ap-btn-green ap-btn-full"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Listing…" : "List Product"}
            </button>
          </div>
        </div>
      </div>

      {/* Authenticity confirmation modal */}
      {showAuthModal && (
        <div
          className="ap-modal-overlay"
          onClick={() => setShowAuthModal(false)}
        >
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-icon" style={{ fontSize: 28 }}>
              ⚖️
            </div>
            <h3 className="ap-modal-title">Authenticity Agreement</h3>
            <p className="ap-modal-body">
              By listing this product, you confirm it is{" "}
              <strong>100% authentic and as described</strong>. TrendBay's motto
              is <em>"Where Quality Sets the Trend"</em> - we hold all sellers
              to this standard.
              <br />
              <br />
              Selling counterfeit, fake, or misrepresented items will result in
              an <strong>immediate ban</strong> and may lead to{" "}
              <strong>legal action</strong>.
            </p>
            <div className="ap-modal-actions">
              <button
                className="ap-modal-cancel"
                onClick={() => setShowAuthModal(false)}
              >
                Cancel
              </button>
              <button
                className="ap-modal-confirm text-white"
                style={{ background: "#00A855" }}
                onClick={confirmAndList}
              >
                I Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
